var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Renderable } from "../rendering/renderable/Renderable";
import { Group, Quaternion } from "three";
import { Vector3 } from "flavr-math";
import { AvatarLoadedMessage, LoadAvatarMessage } from "../../messages/rendering/RendererMessages";
import { AvatarShoulder } from "./AvatarShoulder";
import { AvatarAudio } from "./AvatarAudio";
import { portraitTextureService } from "../rendering/texture/PortraitTextureService";
import { AvatarHead } from "./AvatarHead";
import { applyColorsToMesh } from "./utils";
import { localPlayerContainer } from "../localplayer/LocalPlayerContainer";
import { clockService } from "../xr/ClockService";
import { AvatarAccessoires } from "./AvatarAccessoires";
import { Text } from "troika-three-text";
import { remoteStreamingService } from "../multiplayer/peer";
const AVATAR_NAME_OFFSET = new Vector3(0, 0.3, 0);
const AVATAR_NAME_UNKNOWN = "anonymous";
const tmpVector3One = new Vector3();
const tmpQuaternionOne = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), Math.PI);
const UNIT_Y = new Vector3(0, 1, 0);
/**
 * Avatar representation
 */
export class AvatarRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.type = AvatarRenderable.typestatic;
        this.mainGroup = new Group();
        this.usernameMeshGroup = new Group();
        this.usernameVisible = true;
        this.lastUsernameUpdate = 0;
        this.accessoires = new AvatarAccessoires();
        this.videoElement = undefined;
        this.currentStreamId = undefined;
        this.update = () => {
            this.updateUsernameTransform();
        };
        this.onAccessoirLoaded = () => {
            this.head.setHairVisibility(!this.accessoires.shouldHideHair());
        };
        this.onAccessoirDestroyed = () => {
            this.head.setHairVisibility(!this.accessoires.shouldHideHair());
        };
        this.mainGroup.visible = false;
        this.mainGroup.matrixAutoUpdate = false;
        this.head = new AvatarHead(this.mainGroup);
        this.shoulder = new AvatarShoulder();
        this.owner.addObjectToScene(this.shoulder.group);
        this.avatarAudio = new AvatarAudio(this.owner.modelUID /*, this.mainGroup */); // TODO: mainGroup will be needed for Positional Audio
        this.owner.addObjectToScene(this.mainGroup);
        this.registerAnimationCallback(this.update);
        remoteStreamingService.onUpdateDelegate.add(this.initializeVideoElement.bind(this));
        this.initializeVideoElement();
    }
    removeName() {
        if (this.usernameMesh) {
            this.usernameMeshGroup.remove(this.usernameMesh);
            this.owner.removeObjectFromScene(this.usernameMeshGroup);
            this.usernameMesh.geometry.dispose();
            if (!Array.isArray(this.usernameMesh.material)) {
                this.usernameMesh.material.dispose();
            }
            else {
                for (const material of this.usernameMesh.material) {
                    material.dispose();
                }
            }
            this.usernameMesh = undefined;
        }
    }
    destroy() {
        this.unregisterAnimationCallback();
        this.avatarAudio.destroy();
        this.removeName();
        this.head.destroy();
        this.shoulder.destroy();
        this.owner.removeObjectFromScene(this.shoulder.group);
        this.owner.removeObjectFromScene(this.mainGroup);
        this.destroyVideoElement();
        remoteStreamingService.onUpdateDelegate.remove(this.initializeVideoElement.bind(this));
    }
    setVisible(visible) {
        this.mainGroup.visible = visible;
        this.shoulder.setVisible(visible);
        if (this.usernameMesh) {
            this.usernameMesh.visible = visible;
        }
    }
    needsNewMesh(newFacet) {
        return !this.facet || this.facet.username !== newFacet.username || this.facet.avatarStyle !== newFacet.avatarStyle;
    }
    needsNewVideo(newFacet) {
        return newFacet.videoUrl && (!this.facet || this.facet.videoUrl !== newFacet.videoUrl);
    }
    needsNewColor(newFacet) {
        return !this.facet || this.facet.baseColor !== newFacet.baseColor;
    }
    needsNewUsername(newFacet) {
        return !this.facet || this.facet.displayName !== newFacet.displayName || this.facet.username !== newFacet.username;
    }
    updateFacet(newFacet) {
        var _a;
        const needsNewMesh = this.needsNewMesh(newFacet);
        const hasUsernameChanged = this.needsNewUsername(newFacet);
        const hasVideoChanged = this.needsNewVideo(newFacet);
        const hasColorChanged = this.needsNewColor(newFacet);
        if (hasUsernameChanged) {
            this.createAvatarName((_a = newFacet.displayName) !== null && _a !== void 0 ? _a : newFacet.username);
        }
        if (needsNewMesh) {
            void this.requestMesh(newFacet.username, newFacet.avatarStyle);
        }
        if (hasVideoChanged) {
            this.initializeVideoElement(newFacet);
        }
        if (hasUsernameChanged) {
            this.applyUserImage(newFacet);
        }
        if (hasColorChanged) {
            const headMesh = this.head.getMesh();
            if (headMesh !== undefined) {
                applyColorsToMesh(headMesh, newFacet.baseColor);
            }
            const shoulderGroup = this.shoulder.group;
            applyColorsToMesh(shoulderGroup, newFacet.baseColor);
        }
        this.accessoires.removeAll();
        if (newFacet.params) {
            this.applyAccessoires();
        }
    }
    applyAccessoires() {
        var _a, _b;
        if (((_b = (_a = this === null || this === void 0 ? void 0 : this.currentFacet) === null || _a === void 0 ? void 0 : _a.params) === null || _b === void 0 ? void 0 : _b.accessoires) == null)
            return;
        for (const accessoire of this.currentFacet.params.accessoires) {
            this.addAccessoire(accessoire.id, accessoire.color);
        }
    }
    addAccessoire(id, color) {
        if (!this.currentFacet)
            return;
        const accessoire = this.accessoires.add(id);
        if (!accessoire)
            return;
        accessoire.setColor(color);
        accessoire.onLoadedDelegate.add(this.onAccessoirLoaded);
        accessoire.onDestroyedDelegate.add(this.onAccessoirDestroyed);
        const groupNode = this.getAccessoirGroupNode(accessoire.def.group);
        if (groupNode) {
            groupNode.add(accessoire.group);
        }
        accessoire.applyToTargetPoint(this.currentFacet.avatarStyle);
        accessoire
            .load()
            .then(() => { })
            .catch((error) => {
            console.error(error);
        });
    }
    applyUserImage(newFacet) {
        this.head.userImage = portraitTextureService.getUserTexture(newFacet.username);
    }
    requestMesh(username, avatarStyle) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const msg = yield this.owner.renderer.getConnection().askByMessageType(new LoadAvatarMessage(username, avatarStyle), AvatarLoadedMessage);
                this.handleAvatarLoadedMessage(msg);
            }
            catch (messages) {
                console.error(`Failed on load avatar ${username} with modelUID ${this.owner.modelUID}`, messages);
            }
        });
    }
    updateTransform() {
        this.updateMainTransform();
        this.shoulder.updateTransform(this.owner.worldPosition, this.owner.worldOrientation, this.owner.worldScale);
        this.updateUsernameTransform(true);
    }
    updateMainTransform() {
        this.mainGroup.matrix.copy(this.owner.matrixWorld);
        this.mainGroup.updateMatrixWorld(true);
    }
    updateUsernameTransform(force = false) {
        if (!this.usernameMesh)
            return;
        const time = clockService.getElapsedTime();
        if (!force && time - this.lastUsernameUpdate < 1)
            return;
        this.lastUsernameUpdate = time;
        const dir = tmpVector3One.subVectors(localPlayerContainer.cameraWorldPosition, this.owner.worldPosition);
        dir.normalize();
        let yaw = Math.atan2(-dir.x, dir.z);
        const snapAngle = Math.PI / 4;
        if (snapAngle) {
            const yawOffset = 0;
            yaw = Math.round(yaw / snapAngle) * snapAngle - yawOffset;
        }
        this.usernameMeshGroup.quaternion.setFromAxisAngle(UNIT_Y, yaw).premultiply(tmpQuaternionOne);
        this.usernameMeshGroup.rotateX(Math.PI);
        this.usernameMeshGroup.updateMatrix();
        this.usernameMeshGroup.position.copy(AVATAR_NAME_OFFSET).add(this.owner.worldPosition);
        this.usernameMeshGroup.updateMatrix();
        this.usernameMeshGroup.updateMatrixWorld(true);
    }
    handleAvatarLoadedMessage(msg) {
        const headMesh = msg.headMesh.clone();
        const shoulderMesh = msg.shoulderMesh.clone();
        const mutedIndicator = headMesh.getObjectByName("IndicatorMuted");
        if (mutedIndicator) {
            this.avatarAudio.setMutedIndicator(mutedIndicator);
        }
        const volumeIndicator = headMesh.getObjectByName("IndicatorVolume");
        if (volumeIndicator) {
            this.avatarAudio.setVolumeIndicator(volumeIndicator);
        }
        if (this.facet) {
            applyColorsToMesh(headMesh, this.facet.baseColor);
            applyColorsToMesh(shoulderMesh, this.facet.baseColor);
        }
        this.head.addMesh(headMesh);
        this.shoulder.addMesh(shoulderMesh);
        this.accessoires.removeAll();
        this.applyAccessoires();
    }
    initializeVideoElement(newFacet) {
        var _a, _b;
        let camStream;
        if ((newFacet === null || newFacet === void 0 ? void 0 : newFacet.videoUrl) === undefined) {
            // videoUrl wins, only get the stream if it is not set
            const searchId = (newFacet === null || newFacet === void 0 ? void 0 : newFacet.ownerId) || ((_a = this.facet) === null || _a === void 0 ? void 0 : _a.ownerId);
            camStream = (_b = remoteStreamingService.getCameraStreams().find((s) => s.clientId === searchId)) === null || _b === void 0 ? void 0 : _b.stream;
            if (!camStream) {
                this.destroyVideoElement();
                this.head.userVideoElement = undefined;
                this.currentStreamId = undefined;
                return;
            }
            else {
                if (this.currentStreamId !== camStream.id) {
                    this.destroyVideoElement();
                    this.currentStreamId = camStream.id;
                }
                else {
                    // videoElement for this avatar's stream has already been created, nothing to do
                    return;
                }
            }
        }
        this.videoElement = document.createElement("video");
        this.videoElement.autoplay = true;
        this.videoElement.muted = true;
        this.videoElement.playsInline = true;
        this.videoElement.style.display = "none";
        // TODO: Check if videoUrl is still relevant after the refactoring of the streaming stuff (Maybe for Replays?)
        if (newFacet === null || newFacet === void 0 ? void 0 : newFacet.videoUrl) {
            this.videoElement.src = newFacet.videoUrl;
            this.videoElement.id = `avatar-${newFacet.videoUrl}`;
        }
        else if (camStream) {
            this.videoElement.srcObject = camStream;
            this.videoElement.id = `avatar-${camStream.id}`;
        }
        else {
            console.error("No video stream available for avatar");
            this.destroyVideoElement();
            return;
        }
        document.body.appendChild(this.videoElement);
        this.videoElement.play().catch((error) => {
            // AbortError is expected when destroyVideoElement() is called before play() resolves
            if (error.name !== "AbortError") {
                console.error("Could not play video element", error);
            }
        });
        this.head.userVideoElement = this.videoElement;
    }
    destroyVideoElement() {
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.src = "";
            this.videoElement.srcObject = null;
            this.videoElement.load();
            document.body.removeChild(this.videoElement);
            this.videoElement = undefined;
        }
    }
    createAvatarName(username) {
        const avatarName = username && username !== "defaultUserImage" ? username : AVATAR_NAME_UNKNOWN;
        this.removeName();
        this.usernameMesh = new Text();
        this.usernameMesh.text = avatarName;
        this.usernameMesh.textAlign = "center";
        this.usernameMesh.anchorX = "center";
        this.usernameMesh.anchorY = "middle";
        this.usernameMeshGroup.scale.set(0.5, 0.5, 0.5);
        this.usernameMesh.outlineColor = 0x000000;
        this.usernameMesh.outlineWidth = 0.0025;
        this.usernameMesh.matrixAutoUpdate = false;
        this.usernameMesh.visible = this.usernameVisible;
        this.usernameMeshGroup.position.copy(AVATAR_NAME_OFFSET);
        this.usernameMeshGroup.updateMatrix();
        this.usernameMesh.sync();
        this.usernameMeshGroup.add(this.usernameMesh);
        this.owner.addObjectToScene(this.usernameMeshGroup);
    }
    onPeerRoomChanged(roomDepthDiff, audible) {
        if (!this.avatarAudio)
            return;
        this.avatarAudio.onPeerRoomChanged(roomDepthDiff, audible);
    }
    onMeshVisibilityChanged(visibility) {
        this.head.setVisible(visibility);
        this.shoulder.setVisible(visibility);
        this.usernameVisible = visibility;
        if (this.usernameMesh) {
            this.usernameMesh.visible = visibility;
        }
    }
    getAccessoirGroupNode(group) {
        switch (group) {
            case "hat":
                return this.head.getMesh();
            case "shoulder":
                return this.shoulder.group;
        }
    }
}
AvatarRenderable.typestatic = "Avatar";
